/**
 * Aggressive Unico SDK Cleanup
 *
 * The unico-webframe SDK has a cleanup bug:
 * - 8 addEventListener but only 7 removeEventListener
 * - Leaves 1 phantom listener per initialization
 * - Uses localStorage/sessionStorage/indexedDB without proper cleanup
 *
 * This causes instability on repeated initializations (reload).
 * Solution: Aggressively drain state before each init.
 */

export const performAggressiveUnicoCleanup = () => {
  try {
    // 1. Clear SDK-related storage (sync part)
    try {
      // Clear localStorage keys that look SDK-related
      const sdkKeys = Object.keys(localStorage).filter(
        (key) =>
          key.toLowerCase().includes('unico') ||
          key.toLowerCase().includes('facetec') ||
          key.toLowerCase().includes('faceworker') ||
          key.toLowerCase().includes('liveness') ||
          key.toLowerCase().includes('dorothy') ||
          key.toLowerCase().includes('ozliveness'),
      );
      sdkKeys.forEach((key) => localStorage.removeItem(key));
    } catch (e) {
      // Ignore localStorage errors
    }

    try {
      // Clear sessionStorage
      const sessionKeys = Object.keys(sessionStorage).filter(
        (key) =>
          key.toLowerCase().includes('unico') ||
          key.toLowerCase().includes('face') ||
          key.toLowerCase().includes('liveness'),
      );
      sessionKeys.forEach((key) => sessionStorage.removeItem(key));
    } catch (e) {
      // Ignore sessionStorage errors
    }

    // 2. Clean up global SDK state
    const globalKeysToClean = ['stream', '__unico', '__face', '__liveness'];
    globalKeysToClean.forEach((key) => {
      try {
        delete (window as any)[key];
      } catch (e) {
        // Ignore delete errors
      }
    });

    // Reset devtools detector (which SDK checks)
    try {
      delete (window as any).__devtools_detector;
    } catch (e) {
      // Ignore
    }

    // 3. Clear indexedDB async (fire and forget, non-blocking)
    if (window.indexedDB && window.indexedDB.databases) {
      window.indexedDB.databases().then((dbs) => {
        dbs.forEach((db) => {
          if (db.name && (db.name.toLowerCase().includes('unico') || db.name.toLowerCase().includes('face'))) {
            try {
              window.indexedDB.deleteDatabase(db.name);
            } catch (e) {
              // Ignore indexedDB delete errors
            }
          }
        });
      }).catch(() => {
        // Ignore indexedDB.databases() errors
      });
    }

    return true;
  } catch (error) {
    console.warn('[Unico] Aggressive cleanup had errors (non-fatal)', error);
    return false;
  }
};

/** Awaitable variant — waits for IndexedDB delete handshakes so the SDK never reads stale DBs. */
export const performAggressiveUnicoCleanupAsync = async (): Promise<void> => {
  performAggressiveUnicoCleanup();

  if (!window.indexedDB?.databases) return;
  try {
    const dbs = await window.indexedDB.databases();
    const targets = dbs.filter(
      (db) =>
        !!db.name &&
        (db.name.toLowerCase().includes('unico') ||
          db.name.toLowerCase().includes('face') ||
          db.name.toLowerCase().includes('oz')),
    );
    await Promise.all(
      targets.map(
        (db) =>
          new Promise<void>((resolve) => {
            try {
              const req = window.indexedDB.deleteDatabase(db.name as string);
              const done = () => resolve();
              req.onsuccess = done;
              req.onerror = done;
              req.onblocked = done;
              setTimeout(done, 1500);
            } catch {
              resolve();
            }
          }),
      ),
    );
  } catch {
    // swallow
  }
};
